import React, { useEffect, useRef, useState } from 'react';
import { 
  BrowserMultiFormatReader, 
  NotFoundException,
  BarcodeFormat,
  DecodeHintType 
} from '@zxing/library';
import { Modal, Button, Alert, Space } from 'antd';
import { CameraOutlined, CloseOutlined, RetweetOutlined, UploadOutlined } from '@ant-design/icons';
import './BarcodeScanner.css';

interface BarcodeScannerProps {
  visible: boolean;
  onScan: (result: string) => void;
  onClose: () => void;
  title?: string;
}

const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  visible,
  onScan,
  onClose,
  title = 'Scan Barcode'
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isScanning, setIsScanning] = useState(false);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('environment');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const codeReaderRef = useRef<BrowserMultiFormatReader | null>(null);

  useEffect(() => {
    if (visible && !codeReaderRef.current) {
      console.log('Initializing BarcodeScanner...');
      // Create reader without hints first to test
      codeReaderRef.current = new BrowserMultiFormatReader();
      console.log('Scanner instance created:', codeReaderRef.current);
      startScanning();
    }

    return () => {
      if (codeReaderRef.current) {
        console.log('Cleaning up scanner...');
        codeReaderRef.current.reset();
        codeReaderRef.current = null;
      }
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [visible, facingMode]);

  const startScanning = async () => {
    if (!videoRef.current || !codeReaderRef.current) return;

    try {
      setError(null);
      setIsScanning(true);

      // Stop existing stream if any
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }

      // Request camera permission
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode }
      });
      
      setStream(mediaStream);
      setHasPermission(true);

      // Get available video devices
      const videoInputDevices = await codeReaderRef.current.listVideoInputDevices();
      
      if (videoInputDevices.length === 0) {
        throw new Error('No camera devices found');
      }

      // Use the first available device (usually back camera on mobile)
      const selectedDeviceId = videoInputDevices[0].deviceId;

      // Don't set hints initially - let it try all formats
      // VIN barcodes are typically Code 39 or Code 128
      console.log('Scanner will try all supported barcode formats');

      console.log('Starting barcode scanner with device:', selectedDeviceId);
      console.log('Video element:', videoRef.current);
      console.log('Video dimensions:', videoRef.current.videoWidth, 'x', videoRef.current.videoHeight);
      
      // Add a frame counter for debugging
      let frameCount = 0;
      let lastLogTime = Date.now();
      
      // Start continuous scanning
      await codeReaderRef.current.decodeFromVideoDevice(
        selectedDeviceId,
        videoRef.current,
        (result, err) => {
          frameCount++;
          const now = Date.now();
          
          // Log every second to show it's scanning
          if (now - lastLogTime > 1000) {
            console.log(`Scanning... (${frameCount} frames processed in last second)`);
            frameCount = 0;
            lastLogTime = now;
          }
          
          if (result) {
            const text = result.getText();
            console.log('🎉 Barcode detected:', text);
            console.log('Result object:', result);
            handleSuccessfulScan(text);
          }
          
          if (err) {
            if (!(err instanceof NotFoundException)) {
              console.error('Scanning error (not NotFoundException):', err);
            }
            // Log NotFoundException every 5 seconds
            else if (now - lastLogTime > 5000) {
              console.log('Still scanning, no barcode found yet...');
            }
          }
        }
      );
      
      console.log('decodeFromVideoDevice started successfully');
    } catch (err) {
      console.error('Camera error:', err);
      setHasPermission(false);
      setIsScanning(false);
      
      const error = err as Error;
      if (error.name === 'NotAllowedError') {
        setError('Camera permission denied. Please allow camera access to scan barcodes.');
      } else if (error.name === 'NotFoundError') {
        setError('No camera found on this device.');
      } else {
        setError('Failed to access camera. Please try again.');
      }
    }
  };

  const handleSuccessfulScan = (text: string) => {
    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
    }
    setIsScanning(false);
    onScan(text);
    onClose();
  };

  const handleClose = () => {
    if (codeReaderRef.current) {
      codeReaderRef.current.reset();
    }
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
    setIsScanning(false);
    onClose();
  };

  const toggleCamera = () => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  };

  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // For barcode scanning, we'd need to process the image file
    // This is a placeholder - you'd need an image-based barcode scanner
    console.log('File upload for barcode scanning not yet implemented');
    // Reset file input
    event.target.value = '';
  };

  const openFileSelector = () => {
    fileInputRef.current?.click();
  };

  const captureFrame = async () => {
    console.log('Manual capture attempt...');
    if (codeReaderRef.current && videoRef.current) {
      try {
        const result = await codeReaderRef.current.decodeOnceFromVideoDevice(undefined, videoRef.current);
        if (result) {
          const text = result.getText();
          console.log('Manual capture successful:', text);
          handleSuccessfulScan(text);
        }
      } catch (err) {
        console.error('Manual capture failed:', err);
      }
    }
  };

  return (
    <Modal
      title={title}
      open={visible}
      onCancel={handleClose}
      footer={[
        <Space key="actions">
          <Button 
            onClick={toggleCamera}
            icon={<RetweetOutlined />}
          >
            Switch Camera
          </Button>
          <Button 
            onClick={openFileSelector}
            icon={<UploadOutlined />}
          >
            Upload Photo
          </Button>
          <Button 
            onClick={captureFrame}
          >
            Capture Frame
          </Button>
          <Button 
            type="primary" 
            onClick={() => handleSuccessfulScan('1HGBH41JXMN109186')}
          >
            Test with Sample VIN
          </Button>
          <Button onClick={handleClose} icon={<CloseOutlined />}>
            Close
          </Button>
        </Space>
      ]}
      width={600}
      centered
      className="barcode-scanner-modal"
    >
      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileUpload}
        style={{ display: 'none' }}
      />
      
      <div className="scanner-container">
        {error ? (
          <Alert
            message="Camera Error"
            description={error}
            type="error"
            showIcon
            icon={<CameraOutlined />}
          />
        ) : (
          <>
            <div className="video-container">
              <video
                ref={videoRef}
                className="scanner-video"
                playsInline
                autoPlay
                muted
              />
              {isScanning && (
                <div className="scan-overlay">
                  <div className="scan-region">
                    <div className="scan-corner top-left" />
                    <div className="scan-corner top-right" />
                    <div className="scan-corner bottom-left" />
                    <div className="scan-corner bottom-right" />
                  </div>
                  <p className="scan-hint">Position barcode within the frame</p>
                  <p style={{ fontSize: 11, marginTop: 10, color: '#888' }}>
                    VIN barcodes: Hold steady, ensure good lighting
                  </p>
                </div>
              )}
            </div>
            {hasPermission === null && (
              <div className="permission-pending">
                <p>Requesting camera permission...</p>
              </div>
            )}
          </>
        )}
      </div>
    </Modal>
  );
};

export default BarcodeScanner;